import { Box, Flex, Spinner } from "@chakra-ui/react";
import { useRef } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppInput from "@/features/ui/app-input";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT,
  isAcceptableBusinessRegistrationCertificateType,
  MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE,
} from "@/shared/constants/business-registration-certificate";

interface SignUpBusinessRegistrationFieldProps {
  value: File | null;
  onChange: (file: File | null) => void;
  onTouch: () => void;
  onInvalidType: () => void;
  onTooLarge: () => void;
  // 파싱 API 응답을 기다리는 동안 파일 교체를 막는다.
  isLoading?: boolean;
}

export default function SignUpBusinessRegistrationField({
  value,
  onChange,
  onTouch,
  onInvalidType,
  onTooLarge,
  isLoading = false,
}: SignUpBusinessRegistrationFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { tTitle } = useI18nHelper();

  const openFilePicker = () => {
    if (isLoading) {
      return;
    }
    // 파일 선택창을 한 번 열면 touched 로 간주해 필수 경고를 노출할 수 있게 한다.
    onTouch();
    fileInputRef.current?.click();
  };

  const resetFileInput = () => {
    // 같은 파일을 다시 선택해도 change 이벤트가 발생하도록 초기화한다.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSelect = (file: File) => {
    resetFileInput();
    if (!isAcceptableBusinessRegistrationCertificateType(file)) {
      onInvalidType();
      return;
    }
    if (file.size > MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE) {
      onTooLarge();
      return;
    }
    onChange(file);
  };

  return (
    <>
      <input
        type={"file"}
        style={{ display: "none" }}
        ref={fileInputRef}
        accept={BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT}
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) {
            handleSelect(event.target.files[0]);
          }
        }}
      />
      <Flex
        flexDirection={"row"}
        alignItems={"center"}
        gap={"8px"}
        position={"relative"}
      >
        {isLoading && (
          <Spinner
            size={"sm"}
            position={"absolute"}
            left={"8px"}
            zIndex={"2"}
          />
        )}
        <Box flexGrow={"1"}>
          {/* 파일을 고른 뒤에도 같은 자리에 파일명만 보여줘 레이아웃이 흔들리지 않게 한다. */}
          <AppInput
            value={value ? value.name : ""}
            isReadOnly={true}
            paddingLeft={isLoading ? "28px" : undefined}
            placeholder={tTitle(
              "auth:sign_up_form.field.business_registration_certificate.placeholder"
            )}
            onClick={openFilePicker}
            _hover={{ cursor: isLoading ? "progress" : "pointer" }}
          />
        </Box>
        <Flex flexShrink={"0"}>
          <AppButton
            variant={"white"}
            size={"small"}
            type={"button"}
            isDisabled={isLoading}
            onClick={openFilePicker}
          >
            {value
              ? tTitle("auth:sign_up_form.button.choose_another_file.do")
              : tTitle("auth:sign_up_form.button.browse.do")}
          </AppButton>
        </Flex>
      </Flex>
    </>
  );
}
